import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useRangeSlider } from "@/components/base/histogram-slider/use-range-slider";
import {
  getFormatter,
  type ValueFormat,
  type ValueFormatterStrategy,
} from "@/components/base/histogram-slider/value-formatter";
import { cx } from "@/utils/cx";

export interface SimpleRangeSliderProps {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  format?: ValueFormat;
  formatter?: ValueFormatterStrategy;
  step?: number;
  className?: string;
  trackClassName?: string;
  inputClassName?: string;
  disabled?: boolean;
  minInputPlaceholder?: string;
  maxInputPlaceholder?: string;
}

export const SimpleRangeSlider = ({
  min,
  max,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  format = "number",
  formatter: customFormatter,
  step = 1,
  className,
  trackClassName,
  inputClassName,
  disabled = false,
  minInputPlaceholder,
  maxInputPlaceholder,
}: SimpleRangeSliderProps) => {
  const formatter = useMemo(() => customFormatter ?? getFormatter(format), [customFormatter, format]);

  // Optimistic UI state - updated immediately during dragging
  const [optimisticMinValue, setOptimisticMinValue] = useState(minValue);
  const [optimisticMaxValue, setOptimisticMaxValue] = useState(maxValue);

  // Sync optimistic values when props change (from outside updates)
  useEffect(() => {
    setOptimisticMinValue(minValue);
  }, [minValue]);

  useEffect(() => {
    setOptimisticMaxValue(maxValue);
  }, [maxValue]);

  const { trackRef, isDragging, handleMinThumbMouseDown, handleMaxThumbMouseDown, getMinPercent, getMaxPercent } =
    useRangeSlider({
      min,
      max,
      minValue: optimisticMinValue,
      maxValue: optimisticMaxValue,
      onMinChange,
      onMaxChange,
      onMinUIUpdate: setOptimisticMinValue,
      onMaxUIUpdate: setOptimisticMaxValue,
      step,
    });

  const [minInputValue, setMinInputValue] = useState(formatter.format(optimisticMinValue));
  const [maxInputValue, setMaxInputValue] = useState(formatter.format(optimisticMaxValue));

  // Keep input values in sync with optimistic slider values during dragging
  useEffect(() => {
    setMinInputValue(formatter.format(optimisticMinValue));
  }, [optimisticMinValue, formatter]);

  useEffect(() => {
    setMaxInputValue(formatter.format(optimisticMaxValue));
  }, [optimisticMaxValue, formatter]);

  const minPercent = getMinPercent();
  const maxPercent = getMaxPercent();

  const handleMinInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMinInputValue(e.target.value);
  }, []);

  const handleMaxInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMaxInputValue(e.target.value);
  }, []);

  const handleMinInputBlur = useCallback(() => {
    const parsed = formatter.parse(minInputValue);
    const clamped = Math.max(min, Math.min(parsed, optimisticMaxValue - step));
    onMinChange(clamped);
    setOptimisticMinValue(clamped);
    setMinInputValue(formatter.format(clamped));
  }, [formatter, minInputValue, min, optimisticMaxValue, step, onMinChange]);

  const handleMaxInputBlur = useCallback(() => {
    const parsed = formatter.parse(maxInputValue);
    const clamped = Math.max(optimisticMinValue + step, Math.min(parsed, max));
    onMaxChange(clamped);
    setOptimisticMaxValue(clamped);
    setMaxInputValue(formatter.format(clamped));
  }, [formatter, maxInputValue, max, optimisticMinValue, step, onMaxChange]);

  const handleMinInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleMinInputBlur();
      }
    },
    [handleMinInputBlur],
  );

  const handleMaxInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleMaxInputBlur();
      }
    },
    [handleMaxInputBlur],
  );

  return (
    <div className={cx("flex items-center gap-4 w-full", disabled && "opacity-50 pointer-events-none", className)}>
      {/* Min Input */}
      <input
        type="text"
        value={minInputValue}
        onChange={handleMinInputChange}
        onBlur={handleMinInputBlur}
        onKeyDown={handleMinInputKeyDown}
        placeholder={minInputPlaceholder}
        disabled={disabled}
        className={cx(
          "w-[70px] px-1.5 py-2 text-center text-[13px] flex-shrink-0 bg-primary",
          "border border-gray-200 rounded-md",
          "outline-none focus:border-gray-400",
          "disabled:bg-gray-50 disabled:cursor-not-allowed",
          inputClassName,
        )}
      />

      {/* Track Container */}
      <div className="flex-1 relative h-8 flex items-center">
        <div ref={trackRef} className={cx("relative w-full h-1 bg-gray-200 rounded-full", trackClassName)}>
          <div
            className="absolute h-full bg-gray-800 rounded-full"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`,
            }}
          />

          {/* Min Thumb */}
          <div
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={optimisticMinValue}
            aria-label="Minimum value"
            aria-disabled={disabled}
            className={cx(
              "absolute w-[18px] h-[18px] bg-white border-2 border-gray-800 rounded-full",
              "top-1/2 -translate-y-1/2 -translate-x-1/2",
              optimisticMinValue === optimisticMaxValue ? "z-20" : "z-10",
              !disabled && "cursor-pointer hover:shadow-[0_0_0_4px_rgba(31,41,55,0.1)]",
              "focus:outline-none focus:shadow-[0_0_0_4px_rgba(31,41,55,0.2)]",
              isDragging === "min" && "shadow-[0_0_0_4px_rgba(31,41,55,0.15)]",
            )}
            style={{ left: `${minPercent}%` }}
            onMouseDown={disabled ? undefined : handleMinThumbMouseDown}
            onTouchStart={disabled ? undefined : handleMinThumbMouseDown}
          />

          {/* Max Thumb */}
          <div
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={optimisticMaxValue}
            aria-label="Maximum value"
            aria-disabled={disabled}
            className={cx(
              "absolute w-[18px] h-[18px] bg-white border-2 border-gray-800 rounded-full",
              "top-1/2 -translate-y-1/2 -translate-x-1/2 z-10",
              !disabled && "cursor-pointer hover:shadow-[0_0_0_4px_rgba(31,41,55,0.1)]",
              "focus:outline-none focus:shadow-[0_0_0_4px_rgba(31,41,55,0.2)]",
              isDragging === "max" && "shadow-[0_0_0_4px_rgba(31,41,55,0.15)]",
            )}
            style={{ left: `${maxPercent}%` }}
            onMouseDown={disabled ? undefined : handleMaxThumbMouseDown}
            onTouchStart={disabled ? undefined : handleMaxThumbMouseDown}
          />
        </div>
      </div>

      {/* Max Input */}
      <input
        type="text"
        value={maxInputValue}
        onChange={handleMaxInputChange}
        onBlur={handleMaxInputBlur}
        onKeyDown={handleMaxInputKeyDown}
        placeholder={maxInputPlaceholder}
        disabled={disabled}
        className={cx(
          "w-[70px] px-1.5 py-2 text-center text-[13px] flex-shrink-0 bg-primary",
          "border border-gray-200 rounded-md",
          "outline-none focus:border-gray-400",
          "disabled:bg-gray-50 disabled:cursor-not-allowed",
          inputClassName,
        )}
      />
    </div>
  );
};
